import type { PersonType } from '@/stores/models/Person';
import { RelationshipType as RelationshipModelType } from '@/stores/models/Relationship';
import type { FamilyStoreType } from '@/stores/FamilyStore';
import { RelationshipType as RelationshipEnum } from 'we-tree-api-client/v2';
import { determinePositions } from './spouses';

/**
 * Represents a parent candidate with their relationship information
 */
interface ParentCandidate {
  parent: PersonType;
  relationshipType: RelationshipEnum; // The enum value: 'biological', 'adoptive', etc.
  relationship: RelationshipModelType; // The RelationshipModel MST instance
}

/**
 * Returns numeric priority for relationship types (higher = more important)
 * Priority order: biological=4, adoptive=3, foster=2, guardian=2, step=1
 */
function getRelationshipTypePriority(relationshipType: RelationshipEnum): number {
  const t = relationshipType as string;
  switch (t) {
    case 'biological':
      return 4;
    case 'adoptive':
      return 3;
    case 'foster':
    case 'guardian':
      return 2;
    case 'step':
      return 1;
    default:
      return 0;
  }
}

/**
 * Phase 1: Collect all parent candidates for a given child
 * Queries all relationships where the child is familyMember2Id and type is parent-child
 */
function collectParentCandidates(childFamilyMemberId: string, familyStore: FamilyStoreType): ParentCandidate[] {
  const candidates: ParentCandidate[] = [];

  // Iterate through all relationships to find parent-child relationships
  familyStore.relationships.forEach((rel) => {
    // Check if this is a parent-child relationship where the child is familyMember2Id
    if (rel.isParentChild && rel.familyMember2Id === childFamilyMemberId) {
      const parent = familyStore.getPerson(rel.familyMember1Id);
      if (parent) {
        candidates.push({
          parent,
          relationshipType: rel.relationshipType as RelationshipEnum,
          relationship: rel
        });
      }
    }
  });

  return candidates;
}

/**
 * Phase 2: Sort parent candidates by relationship type priority (highest first)
 */
function prioritizeByRelationshipType(candidates: ParentCandidate[]): ParentCandidate[] {
  return [...candidates].sort((a, b) => {
    const priorityA = getRelationshipTypePriority(a.relationshipType);
    const priorityB = getRelationshipTypePriority(b.relationshipType);
    // Sort descending (highest priority first)
    return priorityB - priorityA;
  });
}

/**
 * Represents a pair of parents who are both parents of the child AND spouses of each other
 */
interface ParentPair {
  parent1: PersonType;
  parent2: PersonType;
  relationshipType1: RelationshipEnum; // Enum value for parent1's relationship to child
  relationshipType2: RelationshipEnum; // Enum value for parent2's relationship to child
  priority: number; // Calculated priority score
}

/**
 * Phase 3A: Identify parent pairs where both people are parents AND spouses
 * For each candidate, check if their selected spouse is also a parent
 */
function identifyParentPairs(candidates: ParentCandidate[]): ParentPair[] {
  const pairs: ParentPair[] = [];
  const candidateMap = new Map<string, ParentCandidate>();

  // Create a map for quick lookup of candidates by familyMemberId
  candidates.forEach((candidate) => {
    candidateMap.set(candidate.parent.familyMemberId, candidate);
  });

  // Track pairs we've already found to avoid duplicates
  const foundPairs = new Set<string>();

  // For each candidate, check if their selected spouse is also a parent
  candidates.forEach((candidate) => {
    const selectedSpouseId = candidate.parent.selectedSpouseFamilyMemberId;
    if (!selectedSpouseId) {
      return; // No selected spouse
    }

    const spouseCandidate = candidateMap.get(selectedSpouseId);
    if (!spouseCandidate) {
      return; // Selected spouse is not a parent
    }

    // Create a unique key for this pair (sorted IDs to avoid duplicates)
    const pairKey = [candidate.parent.familyMemberId, selectedSpouseId].sort().join('-');
    if (foundPairs.has(pairKey)) {
      return; // Already found this pair
    }

    foundPairs.add(pairKey);

    // Create the pair (we'll calculate priority later)
    pairs.push({
      parent1: candidate.parent,
      parent2: spouseCandidate.parent,
      relationshipType1: candidate.relationshipType,
      relationshipType2: spouseCandidate.relationshipType,
      priority: 0 // Will be calculated in next phase
    });
  });

  return pairs;
}

/**
 * Calculates numeric priority for a parent pair (higher = more important)
 * Priority rules:
 * - Both biological = 100
 * - One biological + other = 80
 * - Both adoptive = 60
 * - One adoptive + other = 50
 * - Both foster / guardian = 40
 * - One foster or guardian + other = 30
 * - Both step = 20
 * - Mixed types = based on highest individual priority
 */
function calculatePairPriority(pair: ParentPair): number {
  const priority1 = getRelationshipTypePriority(pair.relationshipType1);
  const priority2 = getRelationshipTypePriority(pair.relationshipType2);

  // Both biological (priority 4)
  if (priority1 === 4 && priority2 === 4) {
    return 100;
  }

  // One biological + other
  if (priority1 === 4 || priority2 === 4) {
    return 80;
  }

  // Both adoptive (priority 3)
  if (priority1 === 3 && priority2 === 3) {
    return 60;
  }

  // One adoptive + other
  if (priority1 === 3 || priority2 === 3) {
    return 50;
  }

  // Both foster (priority 2)
  if (priority1 === 2 && priority2 === 2) {
    return 40;
  }

  // One foster + other
  if (priority1 === 2 || priority2 === 2) {
    return 30;
  }

  // Both step (priority 1)
  if (priority1 === 1 && priority2 === 1) {
    return 20;
  }

  // Fallback: use average of individual priorities scaled by 10
  return (priority1 + priority2) * 10;
}

/**
 * Phase 3B: Sort parent pairs by calculated priority (highest first)
 */
function prioritizeParentPairs(pairs: ParentPair[]): ParentPair[] {
  // Calculate priority for each pair
  const pairsWithPriority = pairs.map((pair) => ({
    ...pair,
    priority: calculatePairPriority(pair)
  }));

  // Sort by priority descending
  return pairsWithPriority.sort((a, b) => b.priority - a.priority);
}

type SelectDisplayParentsOptions = {
  /**
   * When true, choose the two displayed parents by parent–child relationship type priority
   * (e.g. biological before step) and ignore selected-spouse overrides. Used for the tree
   * anchor’s parents row on initial build so the default view favors biological lineage.
   */
  defaultParentsByRelationshipType?: boolean;
};

/**
 * Phase 4: Select final 2 parents from candidates and pairs
 * Handles all scenarios: 0, 1, 2, 3+ parents
 * Returns tuple of [leftParent, rightParent] or [null, null]
 */
function selectFinalParents(
  candidates: ParentCandidate[],
  pairs: ParentPair[],
  familyStore: FamilyStoreType,
  options?: SelectDisplayParentsOptions
): [PersonType | null, PersonType | null] {
  // Scenario: 0 parents
  if (candidates.length === 0) {
    return [null, null];
  }

  // Scenario: 1 parent
  if (candidates.length === 1) {
    const parent1 = candidates[0].parent;
    const selectedSpouseId = parent1.selectedSpouseFamilyMemberId;

    // Option 1: Use parent's selected spouse (even if not a parent)
    if (selectedSpouseId) {
      const spouse = familyStore.getPerson(selectedSpouseId);
      if (spouse) {
        return [parent1, spouse];
      }
    }

    // Option 2: Use first spouse if no selected spouse
    if (parent1.spouseFamilyMemberIds.length > 0) {
      const firstSpouse = familyStore.getPerson(parent1.spouseFamilyMemberIds[0]);
      if (firstSpouse) {
        return [parent1, firstSpouse];
      }
    }

    // Option 3: Only one parent, no spouse
    return [parent1, null];
  }

  // Scenario: 2+ parents — tree anchor default: top two by relationship type, no spouse overrides
  if (options?.defaultParentsByRelationshipType) {
    return [candidates[0].parent, candidates[1].parent];
  }

  // Scenario: 2+ parents
  // PRIORITY RULE: Explicitly selected spouses ALWAYS take precedence over prioritization logic
  // Strategy:
  // 1. First, check for parents who have selected a spouse who is NOT in candidates (highest priority - explicit user action)
  // 2. Then, check for mutual selections (both parents have selected each other)
  // 3. Finally, check for any explicit selection where selected spouse IS in candidates

  // FIRST PRIORITY: Parents who have selected a spouse who is NOT in candidates
  // This represents an explicit user selection that must override everything else
  for (const candidate of candidates) {
    const selectedSpouseId = candidate.parent.selectedSpouseFamilyMemberId;
    if (selectedSpouseId) {
      const spouseCandidate = candidates.find((c) => c.parent.familyMemberId === selectedSpouseId);
      if (!spouseCandidate) {
        // Selected spouse is not in parent candidates - this is an explicit user selection
        // that MUST be respected even if the spouse is not a parent
        const selectedSpouse = familyStore.getPerson(selectedSpouseId);
        if (selectedSpouse) {
          // Use this parent and their selected spouse (even though spouse is not a parent candidate)
          // This respects the user's explicit spouse selection and overrides all other logic
          return [candidate.parent, selectedSpouse];
        }
      }
    }
  }

  // SECOND PRIORITY: Look for mutual selections (both parents have selected each other)
  for (const candidate of candidates) {
    const selectedSpouseId = candidate.parent.selectedSpouseFamilyMemberId;
    if (selectedSpouseId) {
      const spouseCandidate = candidates.find((c) => c.parent.familyMemberId === selectedSpouseId);
      if (spouseCandidate) {
        // Check if the spouse has also selected this candidate (mutual selection)
        const spouseSelectedCandidateId = spouseCandidate.parent.selectedSpouseFamilyMemberId;
        if (spouseSelectedCandidateId === candidate.parent.familyMemberId) {
          // Mutual selection - prioritize this pair
          return [candidate.parent, spouseCandidate.parent];
        }
      }
    }
  }

  // THIRD PRIORITY: Look for explicit selections where selected spouse IS in candidates
  for (const candidate of candidates) {
    const selectedSpouseId = candidate.parent.selectedSpouseFamilyMemberId;
    if (selectedSpouseId) {
      const spouseCandidate = candidates.find((c) => c.parent.familyMemberId === selectedSpouseId);
      if (spouseCandidate) {
        // Found a pair where one parent has explicitly selected the other as spouse
        // Use this pair regardless of relationship priority
        return [candidate.parent, spouseCandidate.parent];
      }
    }
  }

  // NO EXPLICIT SPOUSE SELECTIONS: Use prioritization logic
  // Check if we have a high-priority pair
  if (pairs.length > 0) {
    const topPair = pairs[0];
    // If we have exactly 2 parents and they form a pair, use the pair
    if (candidates.length === 2) {
      return [topPair.parent1, topPair.parent2];
    }

    // If we have 3+ parents, prefer the pair if it's high priority
    // (Both biological = 100, one biological + other = 80)
    if (topPair.priority >= 80) {
      return [topPair.parent1, topPair.parent2];
    }

    // If top candidate has a spouse in top 5 candidates, prefer that combination
    const topCandidate = candidates[0];
    const top5CandidateIds = new Set(candidates.slice(0, 5).map((c) => c.parent.familyMemberId));
    const topCandidateSpouseId = topCandidate.parent.selectedSpouseFamilyMemberId;

    if (topCandidateSpouseId && top5CandidateIds.has(topCandidateSpouseId)) {
      const spouseCandidate = candidates.find((c) => c.parent.familyMemberId === topCandidateSpouseId);
      if (spouseCandidate) {
        return [topCandidate.parent, spouseCandidate.parent];
      }
    }
  }

  // Scenario: 2 parents, no pair found, or 3+ parents without high-priority pair
  // Use top 2 from sorted candidates
  const parent1 = candidates[0].parent;
  const parent2 = candidates.length > 1 ? candidates[1].parent : null;

  // If we have 2 parents, check if parent1 has a spouse who is also a parent
  if (parent2) {
    const parent1SpouseId = parent1.selectedSpouseFamilyMemberId;
    if (parent1SpouseId && parent1SpouseId === parent2.familyMemberId) {
      // They're already a pair, return as-is
      return [parent1, parent2];
    }
  }

  return [parent1, parent2];
}

/**
 * Main entry point: Select and position the 2 parents to display for a given child
 * Orchestrates all phases of the parent selection strategy
 * Reusable for both parents and grandparents generations
 *
 * @param childFamilyMemberId - The family member ID of the child
 * @param familyStore - The family store containing all people and relationships
 * @param options - Optional behavior (e.g. anchor defaults favor biological over step via relationship type)
 * @returns Object with leftParent and rightParent (PersonType instances or null)
 */
export function selectDisplayParents(
  childFamilyMemberId: string,
  familyStore: FamilyStoreType,
  options?: SelectDisplayParentsOptions
): { leftParent: PersonType | null; rightParent: PersonType | null } {
  // Phase 1: Collect all parent candidates
  const candidates = collectParentCandidates(childFamilyMemberId, familyStore);

  // Phase 2: Prioritize by relationship type
  const sortedCandidates = prioritizeByRelationshipType(candidates);

  // Phase 3A: Identify parent pairs
  const parentPairs = identifyParentPairs(sortedCandidates);

  // Phase 3B: Prioritize parent pairs
  const sortedPairs = prioritizeParentPairs(parentPairs);

  // Phase 4: Select final 2 parents
  const [selectedParent1, selectedParent2] = selectFinalParents(sortedCandidates, sortedPairs, familyStore, options);

  // Phase 5: Determine positions (left/right) using existing utility
  const positions = determinePositions(selectedParent1, selectedParent2);

  // Convert from familyMemberId to PersonType
  const leftParent = positions.leftFamilyMemberId ? familyStore.getPerson(positions.leftFamilyMemberId) || null : null;
  const rightParent = positions.rightFamilyMemberId
    ? familyStore.getPerson(positions.rightFamilyMemberId) || null
    : null;

  return { leftParent, rightParent };
}
